import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiFetch, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

interface ConfirmResponse {
  subscriptionStatus: string | null;
  planKey: string | null;
  creditsRemaining: number;
}

const ACTIVE = new Set(['active', 'trialing']);
const MAX_ATTEMPTS = 15; // ~30s at 2s intervals
const INTERVAL_MS = 2000;

// Post-Stripe-checkout landing page. Reconciles the session into our DB immediately
// (independent of the webhook) by calling confirm-session, polling briefly for the
// async case, then routing the user into the app.
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaceId = params.get('workspaceId') || '';
  const sessionId = params.get('session_id') || '';
  const flow = params.get('flow') || 'subscribe';
  const [phase, setPhase] = useState<'working' | 'timeout' | 'auth'>('working');

  useEffect(() => {
    if (!workspaceId || !sessionId) {
      navigate('/app', { replace: true });
      return;
    }

    // StrictMode (dev) runs this effect twice: the first run's cleanup cancels its loop,
    // the second runs to completion. The cancelled flag (not a persistent ref) is what
    // makes that safe — the confirm endpoint is idempotent, so a transient double-call is fine.

    let cancelled = false;
    const dest = flow === 'topup' ? `/w/${workspaceId}/billing` : `/w/${workspaceId}/dashboard`;

    const run = async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          const res = await apiFetch<ConfirmResponse>(`/workspaces/${workspaceId}/billing/confirm-session`, {
            method: 'POST',
            body: JSON.stringify({ checkoutSessionId: sessionId }),
          });
          if (res.subscriptionStatus && ACTIVE.has(res.subscriptionStatus)) {
            if (!cancelled) {
              // The purchase changed the balance: whatever was cached for this workspace is stale.
              queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) });
              queryClient.invalidateQueries({ queryKey: queryKeys.dashboard(workspaceId) });
              toast.success('Payment successful — your subscription is active');
              navigate(dest, { replace: true });
            }
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            if (!cancelled) setPhase('auth');
            return;
          }
          // transient (network / not yet provisioned) — keep polling
        }
        await new Promise((r) => setTimeout(r, INTERVAL_MS));
      }
      if (!cancelled) setPhase('timeout');
    };

    run();
    return () => { cancelled = true; };
  }, [workspaceId, sessionId, flow, navigate, queryClient]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 text-center space-y-4">
        {phase === 'working' && (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-muted-foreground" />
            <h1 className="text-xl font-semibold">Finalizing your subscription…</h1>
            <p className="text-sm text-muted-foreground">Confirming your payment with Stripe. This only takes a moment.</p>
          </>
        )}
        {phase === 'timeout' && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="text-xl font-semibold">Payment received</h1>
            <p className="text-sm text-muted-foreground">
              Provisioning is taking a little longer than usual. It will finish automatically —
              check your billing page in a minute.
            </p>
            <Button onClick={() => navigate(`/w/${workspaceId}/billing`)}>Go to billing</Button>
          </>
        )}
        {phase === 'auth' && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="text-xl font-semibold">Payment received</h1>
            <p className="text-sm text-muted-foreground">
              Your session expired while we finalized the payment. Please log in again —
              your subscription will be active.
            </p>
            <Button onClick={() => navigate('/login')}>Log in</Button>
          </>
        )}
      </Card>
    </div>
  );
}
